/**
 * Tool Results Type Definitions
 *
 * Generic, plugin-based type system for tool result visualization.
 * Supports discriminated unions for type-safe rendering.
 */

// ============================================================================
// Base Tool Result Interface
// ============================================================================

/**
 * Base interface for all tool results.
 * All specific tool result types extend this.
 */
export interface BaseToolResult {
  toolType: string;  // Discriminator for type-safe rendering
  toolName: string;  // Human-readable tool name
}

// ============================================================================
// GoDaddy Tool Result Types
// ============================================================================

/**
 * Domain availability check result (single domain)
 */
export interface DomainAvailabilityResult {
  domain: string;
  available: boolean;
  price?: number;      // Price in cents (e.g., 1199 = $11.99)
  currency?: string;   // USD, EUR, etc.
  error?: string;      // Error message if check failed
}

/**
 * GoDaddy check_availability tool result
 */
export interface GoDaddyAvailabilityToolResult extends BaseToolResult {
  toolType: 'godaddy_check_availability';
  toolName: 'Domain Availability Check';
  available: DomainAvailabilityResult[];
  unavailable: DomainAvailabilityResult[];
  total_checked: number;
}

/**
 * Domain suggestion result (from search)
 */
export interface DomainSuggestion {
  domain: string;
  available: boolean;
  price?: number;      // Price in cents
  currency?: string;
}

/**
 * GoDaddy search_domains tool result
 */
export interface GoDaddySearchToolResult extends BaseToolResult {
  toolType: 'godaddy_search_domains';
  toolName: 'Domain Search';
  suggestions: DomainSuggestion[];
  query: string;
  total_found: number;
}

/**
 * GoDaddy get_pricing tool result
 */
export interface GoDaddyPricingToolResult extends BaseToolResult {
  toolType: 'godaddy_get_pricing';
  toolName: 'Domain Pricing';
  domain: string;
  registration_price?: number;  // Price in cents
  currency?: string;
  available: boolean;
}

// ============================================================================
// RDAP Tool Result Types (AC-1444 / epic AC-1434)
// ============================================================================

/**
 * One row from an RDAP availability check.
 *
 * Mirrors `RDAPDomainResult` in `frontend/src/types/rdap.ts`. We re-declare
 * the minimal shape here to keep `toolResults.ts` self-contained -- the
 * dispatcher only needs to know the discriminator fields, not the full
 * RDAP type surface (the renderer imports the canonical type directly).
 */
export interface RDAPDomainResultLite {
  domain: string;
  status: 'available' | 'taken' | 'unknown';
  reason?: string;
  rdap_server?: string;
  http_status?: number;
  checked_at: string;
}

/**
 * RDAP-based domain availability tool result.
 *
 * Used by both:
 *   - the `rdap_check_availability` agent tool, and
 *   - the `rdap_lookup` orchestration step's persisted RunResult.final_output.
 *
 * Both surfaces emit the same `RDAPLookupResponse` shape (see TD AC-1434
 * section 2.4). `parseToolResult` recognizes the bare shape (no toolType/
 * toolName) by the unique `unknown_count` + `results[]` fields below.
 */
export interface RDAPCheckAvailabilityToolResult extends BaseToolResult {
  toolType: 'rdap_check_availability';
  toolName: 'Domain Availability Check';
  results: RDAPDomainResultLite[];
  total_checked: number;
  available_count: number;
  taken_count: number;
  unknown_count: number;
}

// ============================================================================
// Generic Tool Result Types (for future tools)
// ============================================================================

/**
 * Generic tool result for unknown/unrecognized tools
 * Falls back to text display
 */
export interface GenericToolResult extends BaseToolResult {
  toolType: 'generic';
  toolName: string;
  data: any;  // Raw tool output
}

// ============================================================================
// Discriminated Union
// ============================================================================

/**
 * Union type for all tool results.
 * TypeScript can discriminate based on toolType.
 *
 * Add new tool result types here as they're implemented:
 * | StripePaymentToolResult
 * | SendGridEmailToolResult
 * | TwilioSMSToolResult
 * etc.
 */
export type ToolResult =
  | GoDaddyAvailabilityToolResult
  | GoDaddySearchToolResult
  | GoDaddyPricingToolResult
  | RDAPCheckAvailabilityToolResult
  | GenericToolResult;

// ============================================================================
// Tool Result Detection and Parsing
// ============================================================================

/**
 * Type guard to check if data is a tool result
 */
export function isToolResult(data: any): data is ToolResult {
  return (
    typeof data === 'object' &&
    data !== null &&
    'toolType' in data &&
    'toolName' in data &&
    typeof data.toolType === 'string' &&
    typeof data.toolName === 'string'
  );
}

/**
 * Extract tool type from raw data.
 * Checks multiple locations where tool metadata might exist.
 *
 * @param data - Raw data from run result
 * @returns Tool type string or null if not a tool result
 */
export function extractToolType(data: any): string | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  // Direct toolType property (preferred)
  if ('toolType' in data && typeof data.toolType === 'string') {
    return data.toolType;
  }

  // Check if data has GoDaddy-specific structure patterns
  // This is a fallback for cases where toolType isn't explicitly set
  if ('available' in data && 'unavailable' in data && 'total_checked' in data) {
    return 'godaddy_check_availability';
  }

  if ('suggestions' in data && 'query' in data && 'total_found' in data) {
    return 'godaddy_search_domains';
  }

  if ('domain' in data && 'registration_price' in data && typeof data.available === 'boolean') {
    return 'godaddy_get_pricing';
  }

  // AC-1444 / AC-1456 (epic AC-1434): bare RDAPLookupResponse shape.
  // Distinguished from godaddy_check_availability (which uses
  // `available`/`unavailable` arrays) by the presence of `unknown_count` -
  // three-state (available/taken/unknown) results are unique to RDAP. The
  // step executor stores the array under `data[<output_field>]` while the
  // agent tool returns it as a top-level `results` array; legacy step runs
  // (pre-AC-1456) carry both. Accept any of the three layouts so the parser
  // is robust to all currently-persisted shapes.
  if (
    'total_checked' in data &&
    'available_count' in data &&
    'unknown_count' in data &&
    (
      (Array.isArray(data.results)) ||
      (data.data && typeof data.data === 'object' &&
        Object.values(data.data).some((v) => Array.isArray(v)))
    )
  ) {
    return 'rdap_check_availability';
  }

  return null;
}

/**
 * Parse raw tool output into typed ToolResult.
 * Adds toolType and toolName if missing.
 *
 * @param data - Raw tool output data
 * @returns Typed ToolResult or null if not a tool result
 */
export function parseToolResult(data: any): ToolResult | null {
  const toolType = extractToolType(data);

  if (!toolType) {
    return null;
  }

  // If already has correct structure, return as-is
  if (isToolResult(data)) {
    return data;
  }

  // Add missing metadata based on detected tool type
  switch (toolType) {
    case 'godaddy_check_availability':
      return {
        ...data,
        toolType: 'godaddy_check_availability',
        toolName: 'Domain Availability Check'
      } as GoDaddyAvailabilityToolResult;

    case 'godaddy_search_domains':
      return {
        ...data,
        toolType: 'godaddy_search_domains',
        toolName: 'Domain Search'
      } as GoDaddySearchToolResult;

    case 'godaddy_get_pricing':
      return {
        ...data,
        toolType: 'godaddy_get_pricing',
        toolName: 'Domain Pricing'
      } as GoDaddyPricingToolResult;

    case 'rdap_check_availability':
      // AC-1444 (epic AC-1434): bare or tagged RDAPLookupResponse. Add the
      // discriminator metadata so the registry dispatch in
      // `getRendererForToolType('rdap_check_availability')` returns the
      // RDAP renderer.
      return {
        ...data,
        toolType: 'rdap_check_availability',
        toolName: 'Domain Availability Check'
      } as RDAPCheckAvailabilityToolResult;

    default:
      // Generic fallback
      return {
        toolType: 'generic',
        toolName: toolType,
        data
      } as GenericToolResult;
  }
}

// ============================================================================
// Renderer Props Interface
// ============================================================================

/**
 * Base props interface for all tool result renderers.
 * All renderer components should accept these props.
 */
export interface ToolResultRendererProps<T extends ToolResult = ToolResult> {
  result: T;
  onCopy?: (text: string) => void;
  className?: string;
}
